import isEqual from 'lodash/isEqual';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { ProjectId } from 'api/models';
import { Modal } from 'components/atoms/modal';
import { Select } from 'components/organisms/field-editors/components/components';
import { Task } from 'data/tasks';
import { useUser } from 'hooks/use-user';
import { AvatarOption } from './picker';
import { SquadOptions } from './squad-options';
import { Option, SquadPayload, Value } from './types';
import { useCustomInviteUserToProjectConfirmation } from './use-invite-user';
import { useProjectSquadOptions } from './use-squad-options';
import {
  filterUserOptions,
  nonProjectUsers,
  optionsToSquadPayload,
  squad,
  squadToOptions,
  userOption,
} from './utils';

interface ValidateFollowersProps {
  afterValidate: (squad: SquadPayload) => void;
  followersAsOption: Option[];
  projectId: ProjectId;
}
export const useValidateFollowersBeforeUpdate = ({
  afterValidate,
  projectId,
  followersAsOption,
}: ValidateFollowersProps) => {
  const confirm = useCustomInviteUserToProjectConfirmation(projectId);
  const commit = useCallback(
    (_selected) => {
      afterValidate(optionsToSquadPayload((_selected as Option[]) || []));
    },
    [afterValidate],
  );
  const onCancel = useCallback(
    (_selected: Option[]) => {
      const result = _selected.filter(
        (o) => o.extra.type === 'team' || o.invited,
      );

      if (!isEqual(followersAsOption, result)) {
        commit(result);
      }
    },
    [commit, followersAsOption],
  );

  return useCallback(
    (selected = []) => {
      const nonProjectUsersList = filterUserOptions(
        selected as Option[],
      ).filter(nonProjectUsers);

      if (nonProjectUsersList.length) {
        confirm(
          nonProjectUsersList,
          selected as Option[],
          followersAsOption,
          commit,
          onCancel,
        );
      } else {
        commit(selected);
      }
    },
    [commit, confirm, followersAsOption, onCancel],
  );
};
interface FollowOrUnfollowTaskProps {
  task: Task;
  projectId: ProjectId;
  handleFollowersChange: (squad: SquadPayload) => void;
}
export const useFollowOrUnfollowTask = ({
  task,
  projectId,
  handleFollowersChange,
}: FollowOrUnfollowTaskProps) => {
  const { data: usersAsOptions } = useProjectSquadOptions(
    projectId,
    task.private,
  );
  const { user } = useUser();
  const taskFollowersAsSquadOptions = useMemo(
    () => squadToOptions(squad(task.followers)),
    [task.followers],
  );
  const currentUserFollowingTask = useMemo(
    () =>
      !!taskFollowersAsSquadOptions.find(
        (option) =>
          option.extra.type === 'user' && option.extra.id === user.userId,
      ),
    [taskFollowersAsSquadOptions, user.userId],
  );
  const validateAndUpdateFollower = useValidateFollowersBeforeUpdate({
    afterValidate: handleFollowersChange,
    projectId,
    followersAsOption: taskFollowersAsSquadOptions,
  });
  const followTask = useCallback(() => {
    const invited = !!usersAsOptions?.users?.find(
      (x) => x.extra.type === 'user' && x.extra.id === user.userId,
    );

    validateAndUpdateFollower(
      taskFollowersAsSquadOptions.concat(userOption(user, invited)),
    );
  }, [
    taskFollowersAsSquadOptions,
    user,
    usersAsOptions.users,
    validateAndUpdateFollower,
  ]);
  const unfollowTask = useCallback(() => {
    validateAndUpdateFollower(
      taskFollowersAsSquadOptions.filter(
        (option) =>
          option.extra.type === 'team' || option.extra.id !== user.userId,
      ),
    );
  }, [taskFollowersAsSquadOptions, user.userId, validateAndUpdateFollower]);

  return {
    unfollowTask,
    followTask,
    currentUserFollowingTask,
    taskFollowersAsSquadOptions,
  };
};

const StyledSelect = styled(Select)`
  height: 300px;
`;

interface ManageTaskFollowersModalProps {
  visible: boolean;
  close: VoidFunction;
  value: Option[];
  projectId: ProjectId;
  onChange: (value: SquadPayload) => void;
  isPrivate?: boolean;
}
export const ManageTaskFollowersModal: FC<ManageTaskFollowersModalProps> = ({
  value,
  projectId,
  isPrivate,
  onChange,
  visible,
  close,
}) => {
  const [selected, setSelected] = useState<Value>(value);
  const validateFollowers = useValidateFollowersBeforeUpdate({
    afterValidate: onChange,
    projectId,
    followersAsOption: value,
  });

  useDeepCompareEffect(() => {
    setSelected(value);
  }, [value]);
  const onOk = useCallback(() => {
    if (!isEqual(value, selected)) {
      validateFollowers(selected);
    }
    close();
  }, [close, selected, validateFollowers, value]);
  const onCancel = useCallback(() => {
    setSelected(value);
    close();
  }, [close, value]);

  return (
    <Modal
      visible={visible}
      title={<FormattedMessage id="task.manage_followers" />}
      onOk={onOk}
      onCancel={onCancel}
      okText={<FormattedMessage id="done" />}
      destroyOnClose
    >
      <SquadOptions
        projectId={projectId}
        selected={value}
        isPrivate={isPrivate}
      >
        {({ data: options, isLoading, isFetching }) => (
          <StyledSelect
            isMulti
            autoFocus
            menuIsOpen
            components={{
              Option: AvatarOption,
            }}
            controlShouldRenderValue={false}
            hideSelectedOptions={false}
            isClearable={false}
            options={options}
            value={selected}
            isLoading={isLoading || isFetching}
            onChange={setSelected}
          />
        )}
      </SquadOptions>
    </Modal>
  );
};
